import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import StudioDetail from "./pages/StudioDetail";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import Layout from "@/components/layout/Layout";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import StudiosPage from "./pages/Studios";
import ServicesPage from "./pages/Services";
import GalleryPage from "./pages/Gallery";
import { BlogIndex, BlogPost } from "./pages/Blog";
import ShopPage from "./pages/Shop";
import ShopProductPage from "./pages/ShopProduct";
import AboutPage from "./pages/About";
import ContactPage from "./pages/Contact";
import RatesPage from "./pages/Rates";
import StudioRulesPage from "./pages/StudioRules";
import PhotographyServicesPage from "./pages/PhotographyServices";
import ProductionServicePage from "./pages/ProductionService";
import PostProductionPage from "./pages/PostProduction";
import ColumbusGaPhotoStudio from "./pages/ColumbusGaPhotoStudio";
import BookingPage from "./pages/Booking";
import BookingSuccess from "./pages/BookingSuccess";
import LoginPage from "./pages/Login";
import SignupPage from "./pages/Signup";
import AccountPage from "./pages/Account";
import Unsubscribe from "./pages/Unsubscribe";
import {
  AdminDashboard, AdminBookings, AdminBlog, AdminGallery, AdminTeam,
  AdminProducts, AdminTestimonials, AdminSubscribers, AdminContact, AdminSettings,
} from "./pages/Admin";
import AdminWpImport from "./pages/AdminWpImport";
import AdminWpImportUpload from "./pages/AdminWpImportUpload";
import AdminWpImportFailures from "./pages/AdminWpImportFailures";
import AdminEmailLog from "./pages/AdminEmailLog";
import AdminStudios from "./pages/AdminStudios";
import AdminRefundPolicies from "./pages/AdminRefundPolicies";
import AdminStripeEvents from "./pages/AdminStripeEvents";
import AdminStripeSync from "./pages/AdminStripeSync";
import AdminCalendar from "./pages/AdminCalendar";
import AdminCustomers from "./pages/AdminCustomers";
import AdminAddons from "./pages/AdminAddons";
import AdminAddonStockAudit from "./pages/AdminAddonStockAudit";
import AdminBookingDetail from "./pages/AdminBookingDetail";
import AdminRefundTest from "./pages/AdminRefundTest";
import AdminNotifications from "./pages/AdminNotifications";
import AdminNotificationPreferences from "./pages/AdminNotificationPreferences";
import AdminNotificationDeliveryLog from "./pages/AdminNotificationDeliveryLog";
import AdminSlotBlockAudit from "./pages/AdminSlotBlockAudit";
import AdminE2ETest from "./pages/AdminE2ETest";
import AdminAvailability from "./pages/AdminAvailability";
import Availability from "./pages/Availability";
import OrderSuccess from "./pages/OrderSuccess";
import { AdminOrders, AdminOrderDetail } from "./pages/AdminOrders";
import AdminLeads from "./pages/AdminLeads";
import AdminPromos from "./pages/AdminPromos";
import AdminLandingPages from "./pages/AdminLandingPages";
import AdminCampaigns from "./pages/AdminCampaigns";
import AdminPromoAnalytics from "./pages/AdminPromoAnalytics";
import AdminUtmBuilder from "./pages/AdminUtmBuilder";
import AdminLeadTimeline from "./pages/AdminLeadTimeline";
import AdminAttributionTest from "./pages/AdminAttributionTest";
import AdminAttributionTestHistory from "./pages/AdminAttributionTestHistory";
import AdminStripeKeySwap from "./pages/AdminStripeKeySwap";
import AdminStripeWebhookStatus from "./pages/AdminStripeWebhookStatus";
import AdminStripeWebhookChecklist from "./pages/AdminStripeWebhookChecklist";
import AdminSlotAttempts from "./pages/AdminSlotAttempts";
import AdminSlotTimeline from "./pages/AdminSlotTimeline";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminProdSmokeTest from "./pages/AdminProdSmokeTest";
import AdminWebhookTimeline from "./pages/AdminWebhookTimeline";
import AdminRunBookingTest from "./pages/AdminRunBookingTest";
import AdminHealth from "./pages/AdminHealth";
import AdminSpam from "./pages/AdminSpam";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import LandingPage from "./pages/LandingPage";
import AdminRoute from "./components/auth/AdminRoute";
import ScrollToTop from "./components/ScrollToTop";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/studios" element={<StudiosPage />} />
            <Route path="/studios/:slug" element={<StudioDetail />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/columbus-photography-service" element={<PhotographyServicesPage />} />
            <Route path="/production-service" element={<ProductionServicePage />} />
            <Route path="/post-production" element={<PostProductionPage />} />
            <Route path="/columbus-ga-photo-studio-rental" element={<ColumbusGaPhotoStudio />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:slug" element={<ShopProductPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/rates" element={<RatesPage />} />
            <Route path="/studio-rules" element={<StudioRulesPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/book/success" element={<BookingSuccess />} />
            <Route path="/availability" element={<Availability />} />
            <Route path="/shop/success" element={<OrderSuccess />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
            <Route path="/admin/bookings/:id" element={<AdminRoute><AdminBookingDetail /></AdminRoute>} />
            <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
            <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
            <Route path="/admin/gallery" element={<AdminRoute><AdminGallery /></AdminRoute>} />
            <Route path="/admin/team" element={<AdminRoute><AdminTeam /></AdminRoute>} />
            <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
            <Route path="/admin/testimonials" element={<AdminRoute><AdminTestimonials /></AdminRoute>} />
            <Route path="/admin/subscribers" element={<AdminRoute><AdminSubscribers /></AdminRoute>} />
            <Route path="/admin/contact" element={<AdminRoute><AdminContact /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
            <Route path="/admin/wp-import" element={<AdminRoute><AdminWpImport /></AdminRoute>} />
            <Route path="/admin/wp-import/upload" element={<AdminRoute><AdminWpImportUpload /></AdminRoute>} />
            <Route path="/admin/wp-import/failures" element={<AdminRoute><AdminWpImportFailures /></AdminRoute>} />
            <Route path="/admin/emails" element={<AdminRoute><AdminEmailLog /></AdminRoute>} />
            <Route path="/admin/studios" element={<AdminRoute><AdminStudios /></AdminRoute>} />
            <Route path="/admin/refund-policies" element={<AdminRoute><AdminRefundPolicies /></AdminRoute>} />
            <Route path="/admin/stripe/events" element={<AdminRoute><AdminStripeEvents /></AdminRoute>} />
            <Route path="/admin/stripe/sync" element={<AdminRoute><AdminStripeSync /></AdminRoute>} />
            <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
            <Route path="/admin/addons" element={<AdminRoute><AdminAddons /></AdminRoute>} />
            <Route path="/admin/addons/audit" element={<AdminRoute><AdminAddonStockAudit /></AdminRoute>} />
            <Route path="/admin/refund-test" element={<AdminRoute><AdminRefundTest /></AdminRoute>} />
            <Route path="/admin/notifications" element={<AdminRoute><AdminNotifications /></AdminRoute>} />
            <Route path="/admin/notifications/preferences" element={<AdminRoute><AdminNotificationPreferences /></AdminRoute>} />
            <Route path="/admin/notifications/deliveries" element={<AdminRoute><AdminNotificationDeliveryLog /></AdminRoute>} />
            <Route path="/admin/availability/audit" element={<AdminRoute><AdminSlotBlockAudit /></AdminRoute>} />
            <Route path="/admin/e2e-test" element={<AdminRoute><AdminE2ETest /></AdminRoute>} />
            <Route path="/admin/availability" element={<AdminRoute><AdminAvailability /></AdminRoute>} />
            <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
            <Route path="/admin/orders/:id" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
            <Route path="/admin/leads" element={<AdminRoute><AdminLeads /></AdminRoute>} />
            <Route path="/admin/promos" element={<AdminRoute><AdminPromos /></AdminRoute>} />
            <Route path="/admin/landing-pages" element={<AdminRoute><AdminLandingPages /></AdminRoute>} />
            <Route path="/admin/campaigns" element={<AdminRoute><AdminCampaigns /></AdminRoute>} />
            <Route path="/admin/promos/analytics" element={<AdminRoute><AdminPromoAnalytics /></AdminRoute>} />
            <Route path="/admin/utm-builder" element={<AdminRoute><AdminUtmBuilder /></AdminRoute>} />
            <Route path="/admin/leads/:id/timeline" element={<AdminRoute><AdminLeadTimeline /></AdminRoute>} />
            <Route path="/admin/attribution-test" element={<AdminRoute><AdminAttributionTest /></AdminRoute>} />
            <Route path="/admin/attribution-test/history" element={<AdminRoute><AdminAttributionTestHistory /></AdminRoute>} />
            <Route path="/admin/stripe/key-swap" element={<AdminRoute><AdminStripeKeySwap /></AdminRoute>} />
            <Route path="/admin/stripe/webhook-status" element={<AdminRoute><AdminStripeWebhookStatus /></AdminRoute>} />
            <Route path="/admin/stripe/webhook-checklist" element={<AdminRoute><AdminStripeWebhookChecklist /></AdminRoute>} />
            <Route path="/admin/stripe/webhook-timeline" element={<AdminRoute><AdminWebhookTimeline /></AdminRoute>} />
            <Route path="/admin/run-booking-test" element={<AdminRoute><AdminRunBookingTest /></AdminRoute>} />
            <Route path="/admin/availability/attempts" element={<AdminRoute><AdminSlotAttempts /></AdminRoute>} />
            <Route path="/admin/availability/timeline" element={<AdminRoute><AdminSlotTimeline /></AdminRoute>} />
            <Route path="/admin/emails/templates" element={<AdminRoute><AdminEmailTemplates /></AdminRoute>} />
            <Route path="/admin/prod-smoke-test" element={<AdminRoute><AdminProdSmokeTest /></AdminRoute>} />
            <Route path="/admin/health" element={<AdminRoute><AdminHealth /></AdminRoute>} />
            <Route path="/admin/spam" element={<AdminRoute><AdminSpam /></AdminRoute>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/p/:slug" element={<LandingPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
